import { useSyncExternalStore } from "react";
import type { BottomBarAction } from "./shell-bottom-bar";

interface RegistrySnapshot {
  owner: string | null;
  actions: BottomBarAction[];
}

let snapshot: RegistrySnapshot = { owner: null, actions: [] };
const listeners = new Set<() => void>();

function emitChange(): void {
  for (const listener of listeners) listener();
}

function visibleActions(actions: BottomBarAction[]): BottomBarAction[] {
  return actions.filter((action) => !action.hidden);
}

function sameActions(a: BottomBarAction[], b: BottomBarAction[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i]!;
    const right = b[i]!;
    if (
      left.id !== right.id ||
      left.label !== right.label ||
      (left.variant ?? "secondary") !== (right.variant ?? "secondary") ||
      Boolean(left.disabled) !== Boolean(right.disabled)
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Called during page render. Mutates the snapshot synchronously so BottomNavBar
 * (a later sibling under Layout) can read it via getSnapshot in the same commit.
 * Pass notify=true from effects when the bar must re-render without a full tree pass.
 */
export function registerBottomBarActions(
  owner: string,
  next: BottomBarAction[],
  notify = false,
): void {
  const actions = visibleActions(next);
  const unchanged = snapshot.owner === owner && sameActions(snapshot.actions, actions);
  // Always keep latest action handlers even when the signature is unchanged.
  snapshot = { owner, actions };
  if (!unchanged && notify) emitChange();
}

export function clearBottomBarActions(owner: string): void {
  if (snapshot.owner !== owner) return;
  snapshot = { owner: null, actions: [] };
  emitChange();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): BottomBarAction[] {
  return snapshot.actions;
}

export function useBottomBarActionsSnapshot(): BottomBarAction[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
